import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../config/api';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  ButtonGroup,
  InputAdornment,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import JDDetailsDialog from '../components/JDDetailsDialog';
import JDEditDialog from '../components/JDEditDialog';
import JDCreationDialog from '../components/JDCreationDialog';
import SnackbarAlert from '../components/SnackbarAlert';
import { jobDescriptionService } from '../services/jobDescriptionService';
import AssignSMEDialog from '../components/AssignSMEDialog';
import { useAuth } from '../hooks/useAuth';
import './JobsPage.css';
import { COLORS } from '../config/colors';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_CHIP_STYLES: Record<string, any> = {
  draft: { color: COLORS.GRAY_500, background: COLORS.GRAY_100 },
  in_progress: { color: COLORS.SECONDARY_RED, background: COLORS.WARNING_LIGHT },
  open: { color: COLORS.SUCCESS_DARK, background: COLORS.SUCCESS_LIGHT },
  closed: { color: COLORS.ERROR_DARK, background: COLORS.ERROR_LIGHT },
  on_hold: { color: COLORS.GRAY_400, background: COLORS.GRAY_100 },
  cancelled: { color: COLORS.GRAY_700, background: COLORS.GRAY_200 },

  // Approval statuses for SME (internal.approvalStatus)
  Approved: { color: COLORS.SUCCESS_DARK, background: COLORS.SUCCESS_LIGHT },
  Pending: { color: COLORS.SECONDARY_RED, background: COLORS.WARNING_LIGHT },
  Rejected: { color: COLORS.ERROR_DARK, background: COLORS.ERROR_LIGHT },
  'Under Review': { color: COLORS.PRIMARY, background: COLORS.INFO_LIGHT },

  // Legacy compatibility
  'Assigned to SME': { color: COLORS.PRIMARY, background: COLORS.INFO_LIGHT },
  'Not Assigned to SME': { color: COLORS.GRAY_500, background: COLORS.GRAY_100 },
  'Reviewed and Ready': { color: COLORS.SUCCESS_DARK, background: COLORS.SUCCESS_LIGHT },

  Default: { color: COLORS.GRAY_900, background: COLORS.GRAY_100 },
};

// status options function to use jobStatuses API data for both roles
const getStatusOptions = (
  userRole: string,
  jobStatuses: any[] = [],
  jobDescriptions: any[] = []
) => {
  const baseOptions = [{ value: '', label: 'All Job Descriptions' }];

  if (userRole === 'sme') {
    //  For SME: Show job status options from jobStatuses API (not approval status)
    const smeStatusOptions = jobStatuses
      .filter(status => status.allowed) // Only show allowed statuses for SME
      .map(status => ({
        value: status.value as string,
        label: status.label as string,
      }));

    return [...baseOptions, ...smeStatusOptions];
  } else {
    // For HR/Admin: Show job status options from jobStatuses API
    const hrStatusOptions = jobStatuses
      .filter(status => status.allowed) // Only show allowed statuses
      .map(status => ({
        value: status.value as string,
        label: status.label as string,
      }));

    return [...baseOptions, ...hrStatusOptions];
  }
};

const JobsPage: React.FC = () => {
  const { user } = useAuth();
  const userRole = user?.role || 'hr';

  // Get current user email from localStorage
  const currentUserEmail = localStorage.getItem('user_email') || user?.email;

  //  All state declarations in one place
  const [jobDescriptions, setJobDescriptions] = useState<any[]>([]);
  const [selectedJD, setSelectedJD] = useState<any>(null);
  const [editJD, setEditJD] = useState<any>(null);
  const [showCreateJD, setShowCreateJD] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedJDs, setSelectedJDs] = useState<string[]>([]);
  const [dialogType, setDialogType] = useState<'details' | 'upload' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [assignSMEJD, setAssignSMEJD] = useState<any>(null);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignJDs, setBulkAssignJDs] = useState<any[]>([]);
  const [cloneJDData, setCloneJDData] = useState<any | null>(null);
  const [allowedJDStatuses, setAllowedJDStatuses] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalJDs: 0,
    assignedToSME: 0,
    notAssigned: 0,
    reviewedAndReady: 0,
    underReview: 0,
    openJobs: 0,
    draftJobs: 0,
    closedJobs: 0,
  });
  const [bulkAssignSMEError, setBulkAssignSMEError] = useState<string | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [jobStatuses, setJobStatuses] = useState<any[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  //  Added missing loading and pagination states
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  //  Consolidated fetch function with role-based API calls
  const fetchJobData = async (page: number = currentPage, size: number = pageSize) => {
    if (isLoadingJobs) return; // Prevent concurrent calls
    setIsLoadingJobs(true);

    try {
      let data;

      if (userRole === 'sme') {
        // For SME, fetch only assigned JDs using emailID
        data = await jobDescriptionService.getAll({
          role: 'sme',
          emailID: currentUserEmail, // Use stored email
          page,
          size,
          type: 'jd', // Exclude events for SME
        });
      } else {
        // For HR/Admin, fetch all JDs without emailID
        data = await jobDescriptionService.getAll({
          page,
          size,
          type: 'jd', // Exclude events for HR/Admin
        });
      }

      const jobs = (data.jobs || []).map(jd => ({
        ...jd,
        id: jd.jobId,
        title: jd.jobTitle,
        //  Normalize job status to lowercase for consistent filtering
        status: (jd.jobStatus || '').toLowerCase(), // Convert to lowercase
        originalJobStatus: jd.jobStatus, // Keep original for display
        // Show "Not Available" when assigneeName is null
        assignedSMEs:
          jd.assigneeName || jd.assigneeEmail
            ? [
                {
                  email: jd.assigneeEmail,
                  name: jd.assigneeName || 'Not Available',
                  username: jd.assigneeEmail,
                  displayName: jd.assigneeName || 'Not Available',
                  status: 'Assigned',
                },
              ]
            : [],
        smeReviewStatus: jd.smeReviewStatus || jd.internal?.smeReviewStatus || 'Under Review',
        location: jd.location,

        //  Display status for both roles should show job status from API
        displayStatus: jd.jobStatus || jd.status || 'draft',

        // Assignment status for SME column (HR only)
        smeAssignmentStatus:
          jd.assigneeName || jd.assigneeEmail ? 'Assigned to SME' : 'Not Assigned to SME',

        assigneeName: jd.assigneeName,
        assigneeEmail: jd.assigneeEmail,
      }));

      setJobDescriptions(jobs);

      // Update pagination data
      setTotalJobs(data.total || 0);
      setTotalPages(data.pages || Math.ceil((data.total || 0) / size));
      setCurrentPage(data.page || page);

      //  Calculate stats using normalized status values
      const totalJDs = data.total || jobs.length;
      const assignedToSME = jobs.filter(jd => jd.assignedSMEs && jd.assignedSMEs.length > 0).length;
      const notAssigned = jobs.filter(
        jd => !jd.assignedSMEs || jd.assignedSMEs.length === 0
      ).length;
      const reviewedAndReady = jobs.filter(
        jd => jd.smeReviewStatus === 'Reviewed and Ready'
      ).length;
      const underReview = jobs.filter(jd => jd.smeReviewStatus === 'Under Review').length;
      const openJobs = jobs.filter(jd => jd.status === 'open').length; // Use lowercase
      const draftJobs = jobs.filter(jd => jd.status === 'draft').length;
      const closedJobs = jobs.filter(jd => jd.status === 'closed').length;

      setStats({
        totalJDs,
        assignedToSME,
        notAssigned,
        reviewedAndReady,
        underReview,
        openJobs,
        draftJobs,
        closedJobs,
      });
    } catch (err) {
      console.error('Error fetching job data:', err);
      setSnackbar({
        open: true,
        message: 'Unable to load job descriptions. Please check your network or contact admin.',
        severity: 'error',
      });
      setJobDescriptions([]);
      setTotalJobs(0);
      setTotalPages(0);
      setStats({
        totalJDs: 0,
        assignedToSME: 0,
        notAssigned: 0,
        reviewedAndReady: 0,
        underReview: 0,
        openJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
      });
    } finally {
      setIsLoadingJobs(false);
    }
  };

  //  Legacy function for backward compatibility
  const refreshJobDescriptions = async (resetPagination: boolean = true) => {
    if (resetPagination) {
      setCurrentPage(1);
      await fetchJobData(1, pageSize);
    } else {
      await fetchJobData(currentPage, pageSize);
    }
  };

  // Fetch job statuses
  const fetchJobStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const statusData = await jobDescriptionService.getJobStatusList();
      console.log('Fetched job statuses:', statusData);

      setJobStatuses(statusData.all_statuses || []);
    } catch (error) {
      console.error('Error fetching job statuses:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load job statuses. Using default values.',
        severity: 'warning',
      });

      // Set fallback statuses
      setJobStatuses([
        { value: 'draft', label: 'Draft', allowed: true },
        { value: 'open', label: 'Open', allowed: true },
        { value: 'closed', label: 'Closed', allowed: true },
      ]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  //  Pagination handler functions
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchJobData(newPage, pageSize);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchJobData(1, newSize);
  };

  useEffect(() => {
    // Only fetch if user data is available
    if (user && userRole) {
      fetchJobStatuses(); // Fetch statuses first
      fetchJobData(); // Single consolidated fetch call
    }
  }, [userRole, user?.username, user?.id]); // Only depend on the specific user properties we use

  // --- JD Needing Attention ---
  const jdNeedingAttention = jobDescriptions.filter(jd =>
    (jd.assignedSMEs || []).some(
      sme =>
        sme.status === 'Open' && sme.assignedOn && Date.now() - sme.assignedOn > 48 * 60 * 60 * 1000
    )
  );

  // Handler for Clone JD button (HR/Admin only)
  const handleCloneJD = (jd: any) => {
    if (userRole === 'sme' || jd.status !== 'Closed') return;
    setCloneJDData({
      ...jd,
      id: undefined,
      jobId: undefined,
      title: `${jd.title} (Clone)`,
      status: 'Draft',
      assignedSMEs: [],
    });
    setShowCreateJD(true);
  };

  // Handler for JD creation (HR/Admin only)
  const handleAddJD = async (newJD: any) => {
    if (userRole === 'sme') return;

    // Duplicate JD prevention (same title + location)
    const isDuplicate = jobDescriptions.some(
      jd => jd.title === newJD.title && jd.location === newJD.location && !jd.deleted
    );
    if (isDuplicate) {
      setSnackbar({
        open: true,
        message: 'Duplicate JD (same role/location) exists.',
        severity: 'error',
      });
      return;
    }
    await jobDescriptionService.add(newJD);
    await refreshJobDescriptions();
    setShowCreateJD(false);
    setCloneJDData(null); // Reset after use
    setSnackbar({ open: true, message: 'JD created successfully.', severity: 'success' });
  };

  const handleEditJD = (jd: any) => {
    if (userRole === 'sme') {
      // For SME, open the feedback modal instead of edit dialog
      setFeedbackJD(jd);
      setJdFeedbackModal(true);
    } else {
      // For HR/Admin, open the edit dialog
      setEditJD(jd);
    }
  };

  const handleSaveEditJD = async updatedJD => {
    try {
      // Update the JD via API
      await jobDescriptionService.update(updatedJD.id || updatedJD.jobId, updatedJD);

      // Refresh the list to get updated data
      await refreshJobDescriptions();
      setEditJD(null);

      const message =
        userRole === 'sme' ? 'JD review status updated successfully.' : 'JD updated successfully.';

      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err: any) {
      console.error('Error updating JD:', err);
      setSnackbar({
        open: true,
        message: `Failed to update JD: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  // Single JD delete (HR/Admin only)
  const handleDeleteJD = async (jdId: string) => {
    if (userRole === 'sme') return;

    try {
      await jobDescriptionService.delete(jdId);
      await refreshJobDescriptions();
      setSnackbar({ open: true, message: 'JD deleted.', severity: 'success' });
      setSelectedJDs(prev => prev.filter(id => id !== jdId));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete JD.', severity: 'error' });
    }
  };

  // Handler for deleting one or more JDs (HR/Admin only)
  const handleDeleteJDs = async (jdIds: string[]) => {
    if (userRole === 'sme') return;

    try {
      for (const jdId of jdIds) {
        await jobDescriptionService.delete(jdId);
      }
      setSnackbar({
        open: true,
        message: `Deleted ${jdIds.length} job description(s).`,
        severity: 'success',
      });
      await refreshJobDescriptions();
      setSelectedJDs([]);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete job description(s).',
        severity: 'error',
      });
    }
  };

  const handleBulkDeleteJD = async () => {
    console.log('Frontend logic for delete jds');
    if (userRole === 'sme' || selectedJDs.length === 0) return;

    try {
      for (const jdId of selectedJDs) {
        await jobDescriptionService.delete(jdId);
      }
      await refreshJobDescriptions();
      setSelectedJDs([]);
      setSnackbar({ open: true, message: 'Selected JDs deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete selected JDs.', severity: 'error' });
    }
  };

  const handleViewJD = jd => {
    setSelectedJD(jd);
    setDialogType('details');
  };

  // --- Selection helpers (HR/Admin only) ---
  const isAllSelected = selectedJDs.length === jobDescriptions.length && jobDescriptions.length > 0;
  const isSomeSelected = selectedJDs.length > 0 && selectedJDs.length < jobDescriptions.length;

  const handleSelectAllJDs = (checked: boolean) => {
    if (userRole === 'sme') return;

    if (checked) {
      setSelectedJDs(jobDescriptions.map(jd => jd.id));
    } else {
      setSelectedJDs([]);
    }
  };

  const handleJDSelect = (jdId: string, checked: boolean) => {
    if (userRole === 'sme') return;

    setSelectedJDs(prev => (checked ? [...prev, jdId] : prev.filter(id => id !== jdId)));
  };

  const handleCloseDialog = () => {
    setSelectedJD(null);
    setDialogType(null);
  };

  //  Updated filtering logic - both roles filter by job status now
  const filteredJobDescriptions = jobDescriptions
    .filter(jd => {
      // Search by Job ID or Job Title
      const matchesSearch =
        !searchTerm ||
        jd.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        jd.title.toLowerCase().includes(searchTerm.toLowerCase());

      //  Both SME and HR filter by job status now
      let matchesStatus = true;
      if (filterStatus) {
        matchesStatus = jd.status === filterStatus;
      }

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      return (a.id || '').localeCompare(b.id || '');
    });

  // --- SME Assign Handlers (HR/Admin only)
  const handleAssignSME = (jd: any) => {
    if (userRole === 'sme') return;
    setAssignSMEJD(jd);
  };

  //  Handler for assigning SME to a single JD (HR/Admin only)
  const handleAssignSMEToJD = async (smeData: any) => {
    if (userRole === 'sme' || !assignSMEJD) {
      console.error('No JD selected for SME assignment or insufficient permissions');
      return;
    }

    try {
      console.log('SME assignment completed successfully. Refreshing job data...');

      // Show success message with assigned SME details
      const assignedSMENames =
        smeData.assignedSMEs?.map(result => result.assigneeName).join(', ') || 'SME';

      setSnackbar({
        open: true,
        message: `SME "${assignedSMENames}" assigned successfully to JD: ${assignSMEJD.title}`,
        severity: 'success',
      });

      // Refresh the job descriptions to get the updated data from backend
      await fetchJobData();
      setAssignSMEJD(null);
    } catch (err: any) {
      console.error('Failed to refresh after SME assignment:', err);
      setSnackbar({
        open: true,
        message: `SME assigned but failed to refresh data: ${err.message || 'Unknown error'}`,
        severity: 'warning',
      });
    }
  };

  //  : Handler for bulk assign SME to multiple JDs (HR/Admin only)
  const handleBulkAssignSMEs = async (smeData: any) => {
    if (userRole === 'sme') return;

    try {
      // Extract single SME ID/username since API only accepts one at a time
      let smeUsername: string = '';

      if (Array.isArray(smeData) && smeData.length > 0) {
        // Take the first SME from array
        const firstSME = smeData[0];
        smeUsername = firstSME.username || firstSME.id || firstSME;
      } else if (smeData && typeof smeData === 'object') {
        smeUsername = smeData.username || smeData.id || smeData;
      } else if (typeof smeData === 'string') {
        smeUsername = smeData;
      }

      if (!smeUsername) {
        throw new Error('No valid SME username provided');
      }

      console.log(
        'Bulk assigning SME:',
        smeUsername,
        'to JDs:',
        bulkAssignJDs.map(jd => jd.id)
      );

      // Assign the single SME to all selected JDs
      for (const jd of bulkAssignJDs) {
        await jobDescriptionService.assignSME(jd.id, smeUsername);
      }

      await refreshJobDescriptions();
      setBulkAssignDialogOpen(false);
      setBulkAssignJDs([]);

      setSnackbar({
        open: true,
        message: `SME "${smeUsername}" assigned to ${bulkAssignJDs.length} JD${bulkAssignJDs.length > 1 ? 's' : ''}`,
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Failed to bulk assign SME:', err);
      setSnackbar({
        open: true,
        message: `Failed to assign SME: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  // Get role-appropriate stats for display
  const getStatsForRole = () => {
    if (userRole === 'sme') {
      return [
        { label: 'Total Assigned JDs', value: stats.totalJDs, color: COLORS.PRIMARY },
        { label: 'Under Review', value: stats.underReview, color: '#ed6c02' },
        { label: 'Reviewed & Ready', value: stats.reviewedAndReady, color: COLORS.SUCCESS_DARK },
      ];
    } else {
      return [
        { label: 'Total JDs', value: stats.totalJDs, color: COLORS.PRIMARY_BLUE_DARK },
        { label: 'Open Jobs', value: stats.openJobs || 0, color: COLORS.SUCCESS_DARK },
        { label: 'Draft Jobs', value: stats.draftJobs || 0, color: COLORS.GRAY_500 },
        { label: 'Closed Jobs', value: stats.closedJobs || 0, color: COLORS.ERROR_DARK },
      ];
    }
  };

  // Download HR Report function to handle binary files
  const handleDownloadReport = async () => {
    setIsDownloading(true);

    try {
      const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Downloading HR report...');

      const response = await fetch(`${BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.HR}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json', // Keep this for now, but we'll handle any response type
        },
      });

      if (!response.ok) {
        // For non-200 responses, try to get error message
        let errorMessage = `Failed to download report: ${response.status}`;
        try {
          const errorData = await response.text();
          if (errorData) {
            errorMessage = errorData;
          }
        } catch (e) {
          // Ignore JSON parse errors for error responses
        }
        throw new Error(errorMessage);
      }

      // Get the content type to determine how to handle the response
      const contentType = response.headers.get('content-type') || '';
      const contentDisposition = response.headers.get('content-disposition') || '';

      console.log('Response content-type:', contentType);
      console.log('Response content-disposition:', contentDisposition);

      // Extract filename from content-disposition header if available
      let fileName = 'hr-report';
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1].replace(/['"]/g, '');
      } else {
        // Generate filename based on content type and current date
        const currentDate = new Date().toISOString().split('T')[0];
        if (contentType.includes('application/json')) {
          fileName = `hr-report-${currentDate}.json`;
        } else if (
          contentType.includes(
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ) ||
          contentType.includes('application/vnd.ms-excel')
        ) {
          fileName = `hr-report-${currentDate}.xlsx`;
        } else if (contentType.includes('text/csv')) {
          fileName = `hr-report-${currentDate}.csv`;
        } else if (contentType.includes('application/zip')) {
          fileName = `hr-report-${currentDate}.zip`;
        } else {
          fileName = `hr-report-${currentDate}.txt`;
        }
      }

      // Handle different response types
      let blob;
      if (contentType.includes('application/json')) {
        // Handle JSON response
        try {
          const reportData = await response.json();
          console.log('HR Report data:', reportData);
          const jsonString = JSON.stringify(reportData, null, 2);
          blob = new Blob([jsonString], { type: 'application/json' });
        } catch (jsonError) {
          console.error('Failed to parse JSON, treating as binary:', jsonError);
          const arrayBuffer = await response.arrayBuffer();
          blob = new Blob([arrayBuffer], { type: contentType || 'application/octet-stream' });
        }
      } else {
        // Handle binary response (Excel, ZIP, etc.)
        const arrayBuffer = await response.arrayBuffer();
        blob = new Blob([arrayBuffer], { type: contentType || 'application/octet-stream' });
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `HR report downloaded successfully as ${fileName}!`,
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error downloading HR report:', err);
      setSnackbar({
        open: true,
        message: `Failed to download report: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Add JD feedback modal state
  const [jdFeedbackModal, setJdFeedbackModal] = useState(false);
  const [feedbackJD, setFeedbackJD] = useState<any>(null);

  // Add handler for JD feedback
  const handleJDFeedback = (jd: any) => {
    setFeedbackJD(jd);
    setJdFeedbackModal(true);
  };

  // Add handler for submitting JD feedback
  const handleSubmitJDFeedback = async (feedbackData: any) => {
    try {
      console.log('Submitting JD feedback:', feedbackData);

      const token = localStorage.getItem('access_token');
      const jobId = feedbackData.job_id;

      // Use different endpoints based on user role
      const endpoint =
        userRole === 'hr' || userRole === 'admin'
          ? `/feedback/hr/${jobId}`
          : `/feedback/sme/${jobId}`;

      console.log('Using endpoint:', endpoint);

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(
          `Failed to submit feedback: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log('Feedback submitted successfully:', result);

      // If SME feedback, also update the JD status
      if (userRole === 'sme') {
        try {
          const updatedJD = {
            ...feedbackJD,
            smeReviewStatus: 'Reviewed and Ready',
            internal: {
              ...feedbackJD.internal,
              smeReviewStatus: 'Reviewed and Ready',
              smeReviewDate: new Date().toISOString(),
              smeReviewer: user?.username || user?.email,
            },
          };

          await jobDescriptionService.update(jobId, updatedJD);
          console.log('JD status updated after SME feedback');
        } catch (statusError) {
          console.warn('Failed to update JD status after feedback submission:', statusError);
          // Don't fail the whole operation for this
        }
      }

      setSnackbar({
        open: true,
        message: `${userRole === 'sme' ? 'SME technical feedback' : 'HR feedback'} submitted successfully!`,
        severity: 'success',
      });

      setJdFeedbackModal(false);
      setFeedbackJD(null);

      // Refresh the job descriptions
      await refreshJobDescriptions(false);
    } catch (error) {
      console.error('Error submitting JD feedback:', error);
      setSnackbar({
        open: true,
        message: `Failed to submit feedback: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  return (
    <Box className="jobs-page-container">
      <Box className="jobs-page-content">
        <Box className="jobs-header-container">
          <Box className="jobs-header-content">
            <Box className="jobs-header-text">
              <Typography variant="h5" className="jobs-title">
                {userRole === 'sme' ? 'Assigned Job Descriptions' : 'Jobs'}
              </Typography>
              <Typography variant="body1" className="jobs-subtitle">
                {userRole === 'sme'
                  ? 'Review job descriptions and manage candidate resumes assigned to you.'
                  : 'Manage job descriptions and assign SMEs for candidate analysis.'}
              </Typography>
              <Typography variant="body2" className="jobs-description">
                {(() => {
                  if (userRole === 'sme') {
                    // SME-specific messages
                    if (stats.underReview > 0) {
                      return `${stats.underReview} job description${stats.underReview > 1 ? 's' : ''} awaiting your review`;
                    } else if (stats.totalJDs === 0) {
                      return 'No job descriptions assigned to you yet.';
                    } else {
                      return 'All assigned job descriptions are reviewed.';
                    }
                  } else {
                    // HR/Admin messages - check multiple conditions
                    const unassignedJDs = stats.notAssigned || 0;
                    const jdsNeedingAttention = jdNeedingAttention.length;
                    const underReviewJDs = stats.underReview || 0;

                    if (unassignedJDs > 0 && underReviewJDs > 0) {
                      return `${unassignedJDs} JD${unassignedJDs > 1 ? 's' : ''} need SME assignment and ${underReviewJDs} JD${underReviewJDs > 1 ? 's are' : ' is'} under review.`;
                    } else if (unassignedJDs > 0) {
                      return `${unassignedJDs} job description${unassignedJDs > 1 ? 's' : ''} need${unassignedJDs === 1 ? 's' : ''} SME assignment.`;
                    } else if (underReviewJDs > 0) {
                      return `${underReviewJDs} job description${underReviewJDs > 1 ? 's are' : ' is'} under SME review.`;
                    } else if (jdsNeedingAttention > 0) {
                      return `${jdsNeedingAttention} job description${jdsNeedingAttention > 1 ? 's' : ''} need attention from HR.`;
                    } else if (stats.totalJDs === 0) {
                      return 'No job descriptions found. Create your first JD to get started.';
                    } else {
                      return 'All job descriptions are assigned and up to date.';
                    }
                  }
                })()}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} className="jobs-stats-grid">
            {getStatsForRole().map((stat, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card>
                  <CardContent className="jobs-stats-card-content">
                    <Typography
                      variant="h6"
                      sx={{
                        color: stat.color,
                        fontWeight: 600,
                        fontSize: '1.25rem',
                        mb: 0.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Export Button - HR Only */}
            {(userRole === 'hr' || userRole === 'admin') && (
              <Grid item xs={12} sm={6} md={2.4}>
                <Card sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <CardContent
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={
                        isDownloading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <FileUploadIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      onClick={handleDownloadReport}
                      disabled={isDownloading}
                      fullWidth
                      sx={{
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: COLORS.PRIMARY_BLUE_DARK,
                        color: COLORS.PRIMARY_BLUE_DARK,
                        background: 'white',
                        textTransform: 'none',
                        fontSize: '12px',
                        minHeight: '32px',
                        '&:hover': {
                          background: COLORS.PRIMARY_BLUE_DARK,
                          color: 'white',
                        },
                      }}
                    >
                      {isDownloading ? 'Downloading...' : 'Export Report'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          <Paper
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxWidth: '50%',
              width: '100%',
            }}
          >
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={7}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search by Job ID or Job Title..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  fullWidth
                  sx={{ background: 'var(--white)' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: COLORS.PRIMARY_BLUE_DARK }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  label="Job Status"
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  fullWidth
                  sx={{ background: 'var(--white)' }}
                  disabled={loadingStatuses}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ color: COLORS.PRIMARY_BLUE_DARK }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  {getStatusOptions(userRole, jobStatuses, jobDescriptions).map((option, index) => (
                    <MenuItem key={`${option.value}-${index}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* Create JD, Bulk Delete Buttons - HR Only */}
          {userRole === 'hr' && (
            <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateJD(true)}
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                    filter: 'brightness(0.9)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Create New Opportunity
              </Button>
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  disabled={selectedJDs.length === 0}
                  onClick={handleBulkDeleteJD}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    ml: 2,
                    background: selectedJDs.length > 0 ? COLORS.ERROR_DARK : 'var(--white)',
                    color: selectedJDs.length > 0 ? '#fff' : 'COLORS.PRIMARY_BLUE_DARK',
                    border: selectedJDs.length > 0 ? 'none' : '1px solid COLORS.PRIMARY_BLUE_DARK',
                    '&:hover': {
                      background: selectedJDs.length > 0 ? '#b71c1c' : 'var(--light-gray)',
                    },
                  }}
                >
                  Delete Selected JDs
                </Button>
              </Box>
            </Grid>
          )}

          {/* Updated Jobs List Table */}
          <Table>
            <TableHead>
              <TableRow>
                {(userRole === 'hr' || userRole === 'admin') && (
                  <TableCell
                    padding="checkbox"
                    sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected && !isAllSelected}
                      onChange={e => handleSelectAllJDs(e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all job descriptions' }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}>Job ID</TableCell>
                <TableCell sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}>
                  Job Title
                </TableCell>
                <TableCell sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}>
                  {userRole === 'sme' ? 'Job Status' : 'Job Status'}
                </TableCell>
                <TableCell sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}>Actions</TableCell>
                {(userRole === 'hr' || userRole === 'admin') && (
                  <TableCell sx={{ color: COLORS.PRIMARY_DARK, fontWeight: 600 }}>
                    Assigned SMEs
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingJobs ? (
                <TableRow>
                  <TableCell
                    colSpan={userRole === 'hr' || userRole === 'admin' ? 6 : 5}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Loading job descriptions...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {filteredJobDescriptions.map(jd => (
                    <TableRow
                      key={jd.id}
                      hover
                      sx={{
                        background: 'var(--white)',
                        '&:nth-of-type(even)': { background: 'var(--light-gray)' },
                      }}
                    >
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedJDs.includes(jd.id)}
                            onChange={e => handleJDSelect(jd.id, e.target.checked)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'COLORS.PRIMARY_BLUE_DARK' }}
                        >
                          {jd.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={jd.title}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {jd.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            jobStatuses.find(s => s.value === jd.status)?.label ||
                            jd.originalJobStatus ||
                            jd.status
                          }
                          sx={{
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background:
                              STATUS_CHIP_STYLES[jd.status]?.background ||
                              STATUS_CHIP_STYLES.Default.background,
                            color:
                              STATUS_CHIP_STYLES[jd.status]?.color ||
                              STATUS_CHIP_STYLES.Default.color,
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ background: 'var(--light-gray)', py: 1 }}>
                        <ButtonGroup variant="text" size="small" sx={{ gap: 1 }}>
                          {/* For SME: Only View and Edit buttons */}
                          {userRole === 'sme' ? (
                            <>
                              <Tooltip title="Edit JD">
                                <Button
                                  startIcon={<EditIcon fontSize="small" />}
                                  sx={{
                                    color: 'var(--dark-gray)',
                                    fontSize: 13,
                                    borderRadius: 1,
                                    px: 1,
                                    mx: 0.5,
                                    background: 'transparent',
                                    minWidth: 0,
                                    textTransform: 'none',
                                    '&:hover': { background: 'var(--light-gray)' },
                                  }}
                                  onClick={() => handleEditJD(jd)}
                                >
                                  Edit
                                </Button>
                              </Tooltip>
                            </>
                          ) : (
                            /* For HR/Admin: Edit, View, Delete, Assign/Reassign SME */
                            <>
                              <Tooltip title="Edit JD">
                                <Button
                                  startIcon={<EditIcon fontSize="small" />}
                                  sx={{
                                    color: 'var(--dark-gray)',
                                    fontSize: 13,
                                    borderRadius: 1,
                                    px: 1,
                                    mx: 0.5,
                                    background: 'transparent',
                                    minWidth: 0,
                                    textTransform: 'none',
                                    '&:hover': { background: 'var(--light-gray)' },
                                  }}
                                  onClick={() => handleEditJD(jd)}
                                >
                                  Edit
                                </Button>
                              </Tooltip>
                              <Tooltip title="View JD Details">
                                <Button
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  sx={{
                                    color: COLORS.PRIMARY_DARK,
                                    fontSize: 13,
                                    borderRadius: 1,
                                    px: 1,
                                    mx: 0.5,
                                    background: 'transparent',
                                    minWidth: 0,
                                    textTransform: 'none',
                                    '&:hover': { background: 'var(--light-gray)' },
                                  }}
                                  onClick={() => handleViewJD(jd)}
                                >
                                  View
                                </Button>
                              </Tooltip>
                              <Tooltip title="Delete JD">
                                <Button
                                  startIcon={<DeleteIcon fontSize="small" />}
                                  sx={{
                                    color: 'var(--error-red)',
                                    fontSize: 13,
                                    borderRadius: 1,
                                    px: 1,
                                    mx: 0.5,
                                    background: 'transparent',
                                    minWidth: 0,
                                    textTransform: 'none',
                                    '&:hover': { background: 'rgba(231,76,60,0.08)' },
                                  }}
                                  onClick={() => handleDeleteJD(jd.id)}
                                >
                                  Delete
                                </Button>
                              </Tooltip>
                              <Tooltip
                                title={
                                  jd.assigneeEmail
                                    ? `Reassign SME (Currently: ${jd.assigneeName || jd.assigneeEmail})`
                                    : 'Assign SME to this JD'
                                }
                              >
                                <Button
                                  startIcon={<AssignmentIcon fontSize="small" />}
                                  sx={{
                                    color: jd.assigneeEmail ? COLORS.PRIMARY_DARK : '#388e3c',
                                    fontSize: 13,
                                    borderRadius: 1,
                                    px: 1,
                                    mx: 0.5,
                                    background: 'transparent',
                                    minWidth: 0,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                      background: jd.assigneeEmail
                                        ? COLORS.INFO_LIGHT
                                        : COLORS.SUCCESS_LIGHT,
                                    },
                                  }}
                                  onClick={() => handleAssignSME(jd)}
                                >
                                  {jd.assigneeEmail ? 'REASSIGN SME' : 'ASSIGN SME'}
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </ButtonGroup>
                      </TableCell>
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <TableCell>
                          {jd.assigneeEmail && (
                            <Chip
                              key={jd.assigneeEmail}
                              label={`${jd.assigneeName || 'Unknown'} (Assigned)`}
                              size="small"
                              sx={{
                                mr: 0.5,
                                mb: 0.5,
                                backgroundColor: COLORS.SUCCESS_LIGHT,
                                color: COLORS.SUCCESS_DARK,
                              }}
                            />
                          )}
                          {!jd.assigneeEmail && (
                            <Typography variant="caption" color="text.secondary">
                              No SMEs assigned
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!isLoadingJobs && filteredJobDescriptions.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={userRole === 'hr' || userRole === 'admin' ? 6 : 5}
                        sx={{ textAlign: 'center', py: 4 }}
                      >
                        <Typography variant="body1" color="text.secondary">
                          {jobDescriptions.length === 0
                            ? userRole === 'sme'
                              ? 'No job descriptions assigned to you yet.'
                              : 'No job descriptions found. Click "Create New JD" to get started.'
                            : 'No job descriptions match your current search criteria.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {totalJobs > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              mb: 2,
              px: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const hasActiveFilters = searchTerm || filterStatus;

                  if (hasActiveFilters) {
                    return `Showing ${filteredJobDescriptions.length} of ${totalJobs} jobs (filtered)`;
                  } else {
                    const startIndex = (currentPage - 1) * pageSize + 1;
                    const endIndex = Math.min(currentPage * pageSize, totalJobs);
                    return `Showing ${startIndex} to ${endIndex} of ${totalJobs} jobs`;
                  }
                })()}
              </Typography>
            </Box>

            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => handlePageChange(page)}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              disabled={isLoadingJobs}
            />
          </Box>
        )}

        {/* Dialogs */}
        {(userRole === 'hr' || userRole === 'admin') && (
          <JDCreationDialog
            open={showCreateJD}
            onClose={() => {
              setShowCreateJD(false);
              setCloneJDData(null);
            }}
            onCreated={refreshJobDescriptions}
            initialData={cloneJDData}
            infoMessage={
              cloneJDData
                ? `You are cloning JD: "${cloneJDData.title.replace(' (Clone)', '')}"`
                : undefined
            }
          />
        )}

        <JDEditDialog
          jd={editJD}
          open={!!editJD}
          onClose={() => setEditJD(null)}
          onSave={handleSaveEditJD}
          userRole={userRole}
        />

        <JDDetailsDialog
          jd={selectedJD}
          open={dialogType === 'details'}
          onClose={handleCloseDialog}
        />

        {(userRole === 'hr' || userRole === 'admin') && (
          <>
            <AssignSMEDialog
              open={!!assignSMEJD}
              onClose={() => setAssignSMEJD(null)}
              jd={assignSMEJD}
              onAssign={handleAssignSMEToJD}
            />
            {/* Bulk Assign SME Dialog */}
            <AssignSMEDialog
              open={bulkAssignDialogOpen}
              onClose={() => setBulkAssignDialogOpen(false)}
              jd={{ assignedSMEs: bulkAssignJDs.flatMap(jd => jd.assignedSMEs || []) }}
              onAssign={handleBulkAssignSMEs}
            />
          </>
        )}

        <SnackbarAlert
          snackbar={
            bulkAssignSMEError
              ? { open: true, message: bulkAssignSMEError, severity: 'warning' }
              : snackbar
          }
          setSnackbar={snackbarObj => {
            setSnackbar(snackbarObj);
            setBulkAssignSMEError(null);
          }}
        />
      </Box>
    </Box>
  );
};

export default JobsPage;
